import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
    CloudUpload,
    Loader2,
    Save,
    Ruler,
    Scissors,
    Share2,
    DownloadCloud,
} from "lucide-react";
import { listUploads, uploadPlan, deleteUpload, downloadAllUploads } from "../lib/api";
import UploadedFileList from "./UploadedFileList";
import SaveToWorkgroupDialog from "./SaveToWorkgroupDialog";
import CustomerEmailsPanel from "./CustomerEmailsPanel";
import { Switch } from "./ui/switch";
import { toast } from "sonner";

const UNITS_KEY = "planflow.unit_system";

export const EditProjectDialog = ({ open, onOpenChange, project, onSave, onUploaded }) => {
    const [shareOpen, setShareOpen] = useState(false);
    const [name, setName] = useState("");
    const [streetAddress, setStreetAddress] = useState("");
    const [city, setCity] = useState("");
    const [province, setProvince] = useState("");
    const [country, setCountry] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [jobId, setJobId] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [unitSystem, setUnitSystem] = useState("imperial");
    const [autoSplitPdfs, setAutoSplitPdfs] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [files, setFiles] = useState([]);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (project && open) {
            setName(project.name || "");
            // Prefill structured fields; fall back to parsing the legacy
            // comma-joined `location` string so existing projects still
            // populate the form sensibly.
            const legacy = String(project.location || "");
            const parts = legacy.split(",").map((s) => s.trim()).filter(Boolean);
            setStreetAddress(project.street_address || parts[0] || "");
            setCity(project.city || parts[1] || "");
            setProvince(project.province || parts[2] || "");
            setCountry(project.country || parts[3] || "");
            setPostalCode(project.postal_code || parts[4] || "");
            setJobId(project.job_id || "");
            setCustomerEmail(project.customer_email || "");
            setUnitSystem(project.unit_system || "imperial");
            setAutoSplitPdfs(project.auto_split_pdfs !== false);
        }
    }, [project, open]);

    // iter-169 — compose the legacy single-line `location` from the
    // structured fields so older displays (project rows, contacts card,
    // schedules) stay in sync. Format: "street, city, province, country postal".
    const composedLocation = (() => {
        const s = streetAddress.trim();
        const c = city.trim();
        const pv = province.trim();
        const cn = country.trim();
        const p = postalCode.trim();
        const cityLine = [c, pv, cn].filter(Boolean).join(", ");
        return [s, cityLine, p].filter(Boolean).join(", ");
    })();

    const refreshFiles = async () => {
        if (!project?.id) return;
        try {
            const res = await listUploads(project.id);
            setFiles(res);
        } catch {
            /* noop */
        }
    };

    useEffect(() => {
        if (open && project?.id) refreshFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, project?.id]);

    const isValid =
        name.trim().length > 0 &&
        streetAddress.trim().length > 0 &&
        city.trim().length > 0 &&
        jobId.trim().length > 0;

    const isDirty =
        project &&
        (name.trim() !== (project.name || "") ||
            streetAddress.trim() !== (project.street_address || "") ||
            city.trim() !== (project.city || "") ||
            province.trim() !== (project.province || "") ||
            country.trim() !== (project.country || "") ||
            postalCode.trim() !== (project.postal_code || "") ||
            composedLocation !== (project.location || "") ||
            jobId.trim() !== (project.job_id || "") ||
            customerEmail.trim().toLowerCase() !== (project.customer_email || "") ||
            unitSystem !== (project.unit_system || "imperial") ||
            autoSplitPdfs !== (project.auto_split_pdfs !== false));

    const handleSave = async (e) => {
        e.preventDefault();
        if (!isValid || !project) return;
        setSaving(true);
        try {
            await onSave(project.id, {
                name: name.trim(),
                location: composedLocation,
                street_address: streetAddress.trim(),
                city: city.trim(),
                province: province.trim(),
                country: country.trim(),
                postal_code: postalCode.trim(),
                job_id: jobId.trim(),
                customer_email: customerEmail.trim().toLowerCase(),
                unit_system: unitSystem,
                auto_split_pdfs: autoSplitPdfs,
            });
            try {
                localStorage.setItem(UNITS_KEY, unitSystem);
            } catch {
                /* noop */
            }
            onOpenChange(false);
        } finally {
            setSaving(false);
        }
    };

    const handleFiles = async (incoming) => {
        if (!incoming || incoming.length === 0 || !project) return;
        setUploading(true);
        try {
            for (const f of incoming) {
                await uploadPlan(f, project.id);
            }
            await refreshFiles();
            onUploaded && onUploaded();
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(Array.from(e.dataTransfer.files || []));
    };

    const handleDeleteFile = async (file) => {
        try {
            await deleteUpload(file.id);
            setFiles((prev) => prev.filter((f) => f.id !== file.id));
            toast.success(`Deleted ${file.original_filename}`);
            onUploaded && onUploaded();
        } catch (e) {
            toast.error("Couldn't delete file");
        }
    };

    const handleDownloadAll = async () => {
        if (!files.length || downloadingAll) return;
        setDownloadingAll(true);
        try {
            const base = project?.job_id || project?.name || "project";
            const count = await downloadAllUploads(files, `${base}_plans`);
            if (count > 0) {
                toast.success(`Zipped ${count} file${count === 1 ? "" : "s"}`);
            }
        } catch (e) {
            toast.error("Couldn't package files");
        } finally {
            setDownloadingAll(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[560px] max-h-[88vh] overflow-hidden p-4 sm:p-6"
                data-testid="edit-project-dialog"
            >
                <div className="min-w-0 flex flex-col gap-4 max-h-[calc(88vh-2rem)] overflow-y-auto">
                    <DialogHeader className="min-w-0">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <DialogTitle className="text-white">Edit project</DialogTitle>
                                <DialogDescription className="text-slate-400">
                                    Update the project details or attach additional plans.
                                </DialogDescription>
                            </div>
                            {project && !project.workgroup_id && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setShareOpen(true)}
                                    data-testid="btn-share-project"
                                    className="shrink-0 text-slate-300 hover:text-[#00A3FF] border border-[#2A3655] hover:border-[#00A3FF]/60 h-8 px-2"
                                    title="Save to workgroup"
                                >
                                    <Share2 className="w-4 h-4 mr-1.5" />
                                    <span className="text-xs">Workgroup</span>
                                </Button>
                            )}
                        </div>
                    </DialogHeader>

                    <form onSubmit={handleSave} className="space-y-3 min-w-0">
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="edit-project-name"
                                className="text-slate-300 text-xs uppercase tracking-wider"
                            >
                                Customer name
                            </Label>
                            <Input
                                id="edit-project-name"
                                data-testid="input-edit-project-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="edit-project-street"
                                className="text-slate-300 text-xs uppercase tracking-wider"
                            >
                                Street address
                            </Label>
                            <Input
                                id="edit-project-street"
                                data-testid="input-edit-project-street"
                                value={streetAddress}
                                onChange={(e) => setStreetAddress(e.target.value)}
                                placeholder="e.g. 4016 Carlyon Line"
                                className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="edit-project-city"
                                    className="text-slate-300 text-xs uppercase tracking-wider"
                                >
                                    City
                                </Label>
                                <Input
                                    id="edit-project-city"
                                    data-testid="input-edit-project-city"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="e.g. Severn"
                                    className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                                    required
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="edit-project-province"
                                    className="text-slate-300 text-xs uppercase tracking-wider"
                                >
                                    Province / State
                                </Label>
                                <Input
                                    id="edit-project-province"
                                    data-testid="input-edit-project-province"
                                    value={province}
                                    onChange={(e) => setProvince(e.target.value)}
                                    placeholder="e.g. ON"
                                    className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="edit-project-country"
                                    className="text-slate-300 text-xs uppercase tracking-wider"
                                >
                                    Country
                                </Label>
                                <Input
                                    id="edit-project-country"
                                    data-testid="input-edit-project-country"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    placeholder="e.g. Canada"
                                    className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label
                                    htmlFor="edit-project-postal"
                                    className="text-slate-300 text-xs uppercase tracking-wider"
                                >
                                    Postal code
                                </Label>
                                <Input
                                    id="edit-project-postal"
                                    data-testid="input-edit-project-postal"
                                    value={postalCode}
                                    onChange={(e) => setPostalCode(e.target.value)}
                                    placeholder="e.g. L3V 0W1"
                                    className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="edit-project-job-id"
                                className="text-slate-300 text-xs uppercase tracking-wider"
                            >
                                Project ID
                            </Label>
                            <Input
                                id="edit-project-job-id"
                                data-testid="input-edit-project-job-id"
                                value={jobId}
                                onChange={(e) => setJobId(e.target.value)}
                                className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                                required
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label
                                htmlFor="edit-project-customer-email"
                                className="text-slate-300 text-xs uppercase tracking-wider"
                            >
                                Customer email
                                <span className="ml-2 text-[10px] normal-case text-slate-500 font-normal">
                                    (optional — links to your inbox)
                                </span>
                            </Label>
                            <Input
                                id="edit-project-customer-email"
                                type="email"
                                data-testid="input-edit-project-customer-email"
                                placeholder="customer@example.com"
                                value={customerEmail}
                                onChange={(e) => setCustomerEmail(e.target.value)}
                                className="bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]"
                            />
                        </div>

                        <CustomerEmailsPanel
                            address={(project?.customer_email || customerEmail).trim().toLowerCase()}
                        />

                        <div className="space-y-1.5" data-testid="edit-unit-system-section">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
                                <Ruler className="w-3.5 h-3.5 text-[#00A3FF]" />
                                Unit system
                            </Label>
                            <div
                                className="grid grid-cols-2 gap-2 p-1 bg-[#0A1128] border border-[#2A3655] rounded-md"
                                role="radiogroup"
                                aria-label="Unit system"
                            >
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={unitSystem === "imperial"}
                                    onClick={() => setUnitSystem("imperial")}
                                    data-testid="edit-unit-toggle-imperial"
                                    className={`py-2 rounded-sm text-sm font-semibold transition-colors ${
                                        unitSystem === "imperial"
                                            ? "bg-[#007AFF] text-white shadow-[0_0_14px_rgba(0,122,255,0.3)]"
                                            : "text-slate-300 hover:text-white hover:bg-[#1C253C]"
                                    }`}
                                >
                                    Imperial
                                    <span className="block text-[10px] font-normal opacity-80 mt-0.5">
                                        ft · in · lb
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={unitSystem === "metric"}
                                    onClick={() => setUnitSystem("metric")}
                                    data-testid="edit-unit-toggle-metric"
                                    className={`py-2 rounded-sm text-sm font-semibold transition-colors ${
                                        unitSystem === "metric"
                                            ? "bg-[#007AFF] text-white shadow-[0_0_14px_rgba(0,122,255,0.3)]"
                                            : "text-slate-300 hover:text-white hover:bg-[#1C253C]"
                                    }`}
                                >
                                    Metric
                                    <span className="block text-[10px] font-normal opacity-80 mt-0.5">
                                        m · cm · kg
                                    </span>
                                </button>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 rounded-sm border border-[#2A3655] bg-[#0A1128]/60 p-3">
                            <div className="shrink-0 mt-0.5">
                                <Scissors className="w-4 h-4 text-[#00A3FF]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-3">
                                    <Label
                                        htmlFor="edit-project-auto-split"
                                        className="text-slate-200 font-semibold text-sm cursor-pointer"
                                    >
                                        Auto-split multi-page PDFs
                                    </Label>
                                    <Switch
                                        id="edit-project-auto-split"
                                        checked={autoSplitPdfs}
                                        onCheckedChange={setAutoSplitPdfs}
                                        data-testid="edit-toggle-auto-split-pdfs"
                                        className="data-[state=checked]:bg-[#007AFF]"
                                    />
                                </div>
                                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                                    {autoSplitPdfs
                                        ? "New multi-page PDF uploads to this project will be split into pinnable rows."
                                        : "New multi-page PDFs stay as a single row. Existing files aren't re-processed."}
                                </p>
                            </div>
                        </div>

                        <div className="pt-2 border-t border-[#2A3655]">
                            <div className="flex items-center justify-between mb-2 gap-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                    Plans &amp; documents
                                </Label>
                                <div className="flex items-center gap-2">
                                    {files.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleDownloadAll}
                                            disabled={downloadingAll}
                                            data-testid="btn-download-all-plans"
                                            aria-label="Download all plans as ZIP"
                                            title={`Download all ${files.length} file${files.length === 1 ? "" : "s"} as ZIP`}
                                            className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-[#2A3655] text-[#00A3FF] hover:text-white hover:bg-[#007AFF]/20 hover:border-[#00A3FF]/60 transition-colors disabled:opacity-50 disabled:cursor-wait"
                                        >
                                            {downloadingAll ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                                <DownloadCloud className="w-3 h-3" />
                                            )}
                                            <span>
                                                {downloadingAll ? "Zipping..." : "Download all"}
                                            </span>
                                        </button>
                                    )}
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                        {files.length} attached
                                    </span>
                                </div>
                            </div>

                            <div
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setDragging(true);
                                }}
                                onDragLeave={() => setDragging(false)}
                                onDrop={onDrop}
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="edit-upload-dropzone"
                                className={`cursor-pointer rounded-md border-2 border-dashed p-4 text-center transition-colors min-w-0 ${
                                    dragging
                                        ? "border-[#00A3FF] bg-[#007AFF]/10"
                                        : "border-[#2A3655] hover:border-[#3A4A75] bg-[#0A1128]"
                                }`}
                            >
                                <div className="flex items-center justify-center gap-2 text-slate-300 text-sm">
                                    {uploading ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-[#00A3FF]" />
                                    ) : (
                                        <CloudUpload className="w-4 h-4 text-[#00A3FF]" />
                                    )}
                                    <span>
                                        {uploading
                                            ? "Uploading..."
                                            : "Add more plans · click or drop"}
                                    </span>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf"
                                    className="hidden"
                                    data-testid="edit-upload-file-input"
                                    onChange={(e) =>
                                        handleFiles(Array.from(e.target.files || []))
                                    }
                                />
                            </div>

                            {files.length > 0 && (
                                <div className="mt-2">
                                    <UploadedFileList
                                        files={files}
                                        onDelete={handleDeleteFile}
                                        maxHeight="max-h-40"
                                        testIdPrefix="edit-project-file"
                                    />
                                </div>
                            )}
                        </div>

                        <DialogFooter className="gap-2 sm:gap-2 pt-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="btn-cancel-edit-project"
                                className="text-slate-300 hover:text-white hover:bg-[#1C253C]"
                            >
                                Close
                            </Button>
                            <Button
                                type="submit"
                                disabled={!isValid || !isDirty || saving}
                                data-testid="btn-save-edit-project"
                                className="bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold disabled:opacity-40 disabled:bg-[#2A3655] disabled:text-slate-400 disabled:cursor-not-allowed disabled:hover:bg-[#2A3655]"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Save changes
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </div>
            </DialogContent>
            <SaveToWorkgroupDialog
                kind="project"
                itemId={project?.id}
                itemName={project?.name}
                settingKey="share_projects"
                open={shareOpen}
                onOpenChange={setShareOpen}
                onSaved={() => {
                    setShareOpen(false);
                    onOpenChange(false);
                }}
            />
        </Dialog>
    );
};

export default EditProjectDialog;
